'use client';
import { FC, FormEvent, useEffect, useRef, useState } from 'react';

import { Button, Modal } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { NAVIGATION_MODAL_ZINDEX } from '@telialt/common-constants/src/modal';

import { LeadFormType } from '../../../../models/Content';
import { StyledContainer } from '../../../ContentSlot/ContentSlot.styles';
import { LEAD_TEST_IDS } from '../../constants';
import { useGAEvent } from '../../hooks/useGAEvent';
import { useLeadSubmit } from '../../hooks/useLeadSubmit';
import { LeadChildrenProps, LeadFormData } from '../../model';
import { useLeadFormModalControls } from './useLeadFormModalControls';

type Props = LeadChildrenProps &
    Pick<LeadFormType['fields'], 'name' | 'submitButtonLabel' | 'legalText' | 'commentHint'> & {
        uid: string;
        showDefaultOpenModalButton?: boolean;
    };

export const LeadFormModal: FC<Props> = ({
    leadFormContent,
    successNotification,
    errorNotification,
    uid,
    name,
    showDefaultOpenModalButton,
    submitButtonLabel,
    legalText,
    commentHint,
}) => {
    const { t } = useTranslations();
    const { isLeadFormModalOpen, closeLeadFormModal, openLeadFormModal } = useLeadFormModalControls(uid);
    const { submitLead, resetSubmitLead, isPending, isSuccess, isError } = useLeadSubmit();
    const formRef = useRef<HTMLFormElement>(null);

    useEffect(() => {
        if (!isLeadFormModalOpen && (isSuccess || isError)) {
            resetSubmitLead();
        }
    }, [isLeadFormModalOpen, isSuccess, isError, resetSubmitLead]);
    const { isLoading, pushGAEvent } = useGAEvent();
    const [isDirty, setIsDirty] = useState(false);
    const markFormAsDirty = () => {
        if (!isDirty) {
            setIsDirty(true);
            pushGAEvent('formStart');
        }
    };

    useEffect(() => {
        if (!isLoading && isLeadFormModalOpen) {
            pushGAEvent('formOpen');
        }
    }, [isLoading, isLeadFormModalOpen, pushGAEvent]);

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (event.target instanceof HTMLFormElement) {
            const formData: LeadFormData = Object.fromEntries(new FormData(event.target));
            submitLead(
                { ...formData, legalText, comment: formData.comment || commentHint },
                {
                    onSuccess: () =>
                        pushGAEvent('formSubmissionSuccess', {
                            phone: formData?.phoneNumber,
                            email: formData?.emailAddress,
                        }),
                },
            );
        }
    };

    const handleSubmitClick = () => {
        formRef.current?.requestSubmit();
    };

    const getActions = () => {
        const closeButton = (
            <Button
                variant="secondary"
                type="button"
                onClick={closeLeadFormModal}
                data-test={LEAD_TEST_IDS.CLOSE_BUTTON}
            >
                {t['common.button.close']}
            </Button>
        );

        const retryButton = (
            <Button variant="primary" type="button" onClick={resetSubmitLead} data-test={LEAD_TEST_IDS.RETRY_BUTTON}>
                {t['content.lead.retry_button']}
            </Button>
        );

        if (isSuccess) {
            return (
                <>
                    {retryButton}
                    {closeButton}
                </>
            );
        }

        if (isError) {
            return (
                <>
                    {retryButton}
                    {closeButton}
                </>
            );
        }

        return (
            <Button
                variant="primary"
                type="button"
                onClick={handleSubmitClick}
                loading={isPending}
                data-test={LEAD_TEST_IDS.SUBMIT_BUTTON}
            >
                {submitButtonLabel}
            </Button>
        );
    };

    return (
        <>
            <Modal open={isLeadFormModalOpen} onOpenChange={(open) => !open && closeLeadFormModal()}>
                <Modal.Content
                    title={name}
                    closeButtonAriaLabel={t['common.button.close']}
                    showCloseButton={true}
                    zIndex={NAVIGATION_MODAL_ZINDEX}
                    actions={getActions()}
                    stickyButtons={true}
                >
                    <form
                        ref={formRef}
                        onSubmit={handleSubmit}
                        onChange={markFormAsDirty}
                        data-test={LEAD_TEST_IDS.LEAD_FORM}
                    >
                        <div hidden={isSuccess || isError}>{leadFormContent}</div>
                        {isSuccess && successNotification}
                        {isError && errorNotification}
                    </form>
                </Modal.Content>
            </Modal>
            {showDefaultOpenModalButton && (
                <StyledContainer element="section" data-section="Lead Form Modal" data-section-name={name}>
                    <Button
                        variant="primary"
                        onClick={(event) => openLeadFormModal(null, event.currentTarget)}
                        data-test={LEAD_TEST_IDS.OPEN_MODAL_BUTTON}
                    >
                        {t['content.lead.default_open_modal_button']}
                    </Button>
                </StyledContainer>
            )}
        </>
    );
};
